import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { useNavigate } from 'react-router-dom';
import { handleNavigation } from '@/utils/navigation-utils';
import { Card, CardDescription, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AuditorExportDialog } from '@/components/auditor-export/auditor-export-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn, faCircleQuestion, faChartPie, faProjectDiagram, faFileExport, faQuestion, faDownload, faSpinner, faCheck, faXmark, faClock, faExclamationTriangle, faChevronDown } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/datatable';
import { ColumnDef } from '@tanstack/react-table';
import { getAvailableFeatures } from '@/utils/featuresUtil';
import { cn } from "@/lib/utils"

interface ReportJob {
  jobId: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  downloadUrl?: string;
  expiresAt?: string;
  error?: string;
  createdAt?: string;
  completedAt?: string;
}

const ReportActionButton = ({ item, onClick, className }: { item: ReportLibraryCardsItem; onClick: (item: ReportLibraryCardsItem) => void; className?: string }) => {
  const iconMap: Record<string, any> = {
    faChartColumn,
    faCircleQuestion,
    faChartPie,
    faProjectDiagram,
    faFileExport,
  };

  const icon = iconMap[item.icon] || faQuestion;

  return (
    <Button
      variant="outline"
      onClick={() => onClick(item)}
      className={cn("h-auto py-3 px-4 flex items-center gap-3 justify-start", className)}
    >
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="text-left">
        <div className="font-medium">{item.title}</div>
        <div className="text-xs text-muted-foreground">{item.subtitle}</div>
      </div>
    </Button>
  );
};

const StatusBadge = ({ status }: { status: ReportJob['status'] }) => {
  const config: Record<string, { variant: 'secondary' | 'default' | 'destructive'; icon: any; label: string; spin?: boolean; className?: string }> = {
    queued: { variant: 'secondary', icon: faClock, label: 'Queued' },
    processing: { variant: 'default', icon: faSpinner, label: 'Processing', spin: true },
    completed: { variant: 'default', icon: faCheck, label: 'Completed', className: 'bg-green-600 hover:bg-green-700' },
    failed: { variant: 'destructive', icon: faXmark, label: 'Failed' },
  };

  const { variant, icon, label, spin, className } = config[status] || config.queued;

  return (
    <Badge variant={variant} className={className}>
      <FontAwesomeIcon icon={icon} spin={spin} className="mr-1.5" />
      {label}
    </Badge>
  );
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

interface ReportLibraryCardsItem {
  title: string;
  subtitle: string;
  description?: string | null;
  icon: 'faChartColumn' | 'faCircleQuestion' | 'faChartPie' | 'faProjectDiagram' | 'faFileExport';
  url?: string | null;
  routeName?: string | null;
  dialog?: 'auditor-export' | null;
}

const ReportLibrary: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  const [isAuditorExportOpen, setIsAuditorExportOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [reportCards, setReportCards] = useState<ReportLibraryCardsItem[]>([]);
  const [generatedReports, setGeneratedReports] = useState<ReportJob[]>([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [errorDialogJob, setErrorDialogJob] = useState<ReportJob | null>(null);

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<ReportJob>[]>(() => [
    {
      accessorKey: 'jobId',
      header: 'Report ID',
      cell: ({ row }) => (
        <span className="font-mono text-sm" title={row.original.jobId}>
          {row.original.jobId}
        </span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <StatusBadge status={row.original.status} />
          {row.original.status === 'processing' && row.original.progress !== undefined && (
            <span className="text-xs text-muted-foreground">{row.original.progress}%</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      accessorKey: 'completedAt',
      header: 'Completed',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.completedAt)}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => {
        const job = row.original;
        if (job.status === 'completed' && job.downloadUrl) {
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(job.downloadUrl, '_blank')}
            >
              <FontAwesomeIcon icon={faDownload} className="mr-1.5" />
              Download
            </Button>
          );
        }
        if (job.status === 'failed') {
          return (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setErrorDialogJob(job)}
            >
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1.5" />
              Error
            </Button>
          );
        }
        return <span className="text-xs text-muted-foreground">Processing...</span>;
      },
    },
  ], []);

  const fetchGeneratedReports = useCallback(async () => {
    try {
      const response = await deiterateAPI.GET<{ success: boolean; jobs: ReportJob[] }>('/v1/reports/auditor-pack', {
        backend: 'thalassa',
      });
      if (response.success && response.jobs) {
        setGeneratedReports(response.jobs);
      }
    } catch (error) {
      console.error('Error fetching generated reports:', error);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTenancy?.id && !["", "Loading..."].includes(activeTenancy.id)) {
      fetchGeneratedReports();
    }
  }, [activeTenancy?.id, fetchGeneratedReports]);

  // Separate effect for polling when there are pending jobs
  useEffect(() => {
    const hasPendingJobs = generatedReports.some(
      (job) => job.status === 'queued' || job.status === 'processing'
    );

    if (hasPendingJobs) {
      console.log('[ReportLibrary] Starting polling - pending jobs detected');
      const interval = setInterval(() => {
        console.log('[ReportLibrary] Polling for updates...');
        fetchGeneratedReports();
      }, 3000);

      return () => {
        console.log('[ReportLibrary] Stopping polling');
        clearInterval(interval);
      };
    }
  }, [generatedReports, fetchGeneratedReports]);

  const handleCardClick = (item: ReportLibraryCardsItem) => {
    if (item.dialog === 'auditor-export') {
      setIsAuditorExportOpen(true);
    } else if (item.routeName) {
      handleNavigation(item.url, navigate, undefined, item.routeName);
    }
  };

  const handleExportComplete = () => {
    setIsAuditorExportOpen(false);
    fetchGeneratedReports();
  };

  const forISMCustomers: ReportLibraryCardsItem[] = [
    {
      title: 'RFFR Overview',
      subtitle: 'View your RFFR Overview',
      description: 'This overview provides a comprehensive understanding of your current RFFR implementation status.',
      routeName: 'rffrImplementationOverview.html',
      url: '/rffrImplementationOverview.html',
      icon: 'faChartPie'
    },
    {
      title: 'RFFR Project Overview',
      subtitle: 'Overview of your RFFR Project',
      description: 'This overview provides graphs and filters to aid in understanding your current RFFR Project status.',
      routeName: 'rffrProjectOverview.html',
      url: '/rffrProjectOverview.html',
      icon: 'faProjectDiagram',
    }
  ];

  useEffect(() => {
    if (!activeTenancy || ["", "Loading..."].includes(activeTenancy?.id)) {
      setIsLoading(true);
      return;
    }

    const customerPlan = activeTenancy?.plan?.toLowerCase() || '';
    const isStarterPlan = customerPlan.includes('starter');
    const isISMPlan = customerPlan === 'ism';

    // Navigation links (existing reports/pages)
    const navigationLinks: ReportLibraryCardsItem[] = [];

    if (!isStarterPlan) {
      navigationLinks.push({
        title: 'Reading Progress Report',
        subtitle: 'View your reading progress',
        description: 'Use this report to view your users\' reading progress',
        routeName: 'reports',
        url: '/reports',
        icon: 'faChartColumn'
      });
    }

    if (isISMPlan) {
      navigationLinks.push(
        {
          title: 'RFFR Overview',
          subtitle: 'View your RFFR Overview',
          description: 'This overview provides a comprehensive understanding of your current RFFR implementation status.',
          routeName: 'rffrImplementationOverview.html',
          url: '/rffrImplementationOverview.html',
          icon: 'faChartPie'
        },
        {
          title: 'RFFR Project Overview',
          subtitle: 'Overview of your RFFR Project',
          description: 'This overview provides graphs and filters to aid in understanding your current RFFR Project status.',
          routeName: 'rffrProjectOverview.html',
          url: '/rffrProjectOverview.html',
          icon: 'faProjectDiagram',
        }
      );
    }

    // Report generation actions
    const generationActions: ReportLibraryCardsItem[] = [];

    if (!activeTenancy?.disabledFeatures?.includes('auditorPack')) {
      generationActions.push({
        title: 'Auditor Pack',
        subtitle: 'SOA and Assurance Task Export',
        icon: 'faFileExport',
        dialog: 'auditor-export',
      });
    }

    setReportCards([...navigationLinks, ...generationActions]);
    setIsLoading(false);
  }, [activeTenancy]);

  // Separate navigation links from generation actions
  const navigationLinks = reportCards.filter(card => card.url || card.routeName);
  const generationActions = reportCards.filter(card => card.dialog);

  return (
    <div className="p-6 space-y-8">
      {isLoading ? (
        <p className="text-muted-foreground">Loading Report Library...</p>
      ) : (
        <>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
              <div className="space-y-1">
                <CardTitle>Reports & Exports</CardTitle>
                <CardDescription>Access existing reports, generate new exports, and download previous generations</CardDescription>
              </div>
              {generationActions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="default" className="gap-2">
                      <FontAwesomeIcon icon={faFileExport} />
                      Generate Report
                      <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[300px]">
                    {generationActions.map((item, index) => {
                      const iconMap: Record<string, any> = {
                        faChartColumn,
                        faCircleQuestion,
                        faChartPie,
                        faProjectDiagram,
                        faFileExport,
                      };
                      const icon = iconMap[item.icon] || faQuestion;

                      return (
                        <DropdownMenuItem
                          key={index}
                          onClick={() => handleCardClick(item)}
                          className="cursor-pointer p-3"
                        >
                          <div className="flex items-start gap-3 w-full">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                              <FontAwesomeIcon icon={icon} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{item.title}</div>
                              <div className="text-xs text-muted-foreground">{item.subtitle}</div>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Quick Links Section */}
              {navigationLinks.length > 0 && (
                <div className="">
                  <div>
                    <h3 className="text-sm font-semibold mb-1">Quick Links</h3>
                    <p className="text-xs text-muted-foreground">Access existing reports and dashboards</p>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {navigationLinks.map((item, index) => (
                      <ReportActionButton
                        key={index}
                        item={item}
                        onClick={handleCardClick}
                        className="bg-card"
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* Generated Reports Table */}
              <div className="mt-3">
                {reportsLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : generatedReports.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FontAwesomeIcon icon={faFileExport} className="text-4xl mb-3 opacity-50" />
                    <p>No reports generated yet</p>
                    <p className="text-sm">Generate your first report using the buttons above</p>
                  </div>
                ) : (
                  <div className="mt-[-30px]">
                    <DataTable
                      columns={columns}
                      data={generatedReports}
                      pageSize={10}
                      tableId="report-library-generated-reports"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <AuditorExportDialog
        isOpen={isAuditorExportOpen}
        onClose={() => setIsAuditorExportOpen(false)}
        onExportComplete={handleExportComplete}
        onError={(error) => {
          console.error('Export error:', error);
        }}
      />

      {/* Error Dialog */}
      <Dialog open={!!errorDialogJob} onOpenChange={(open) => !open && setErrorDialogJob(null)}>
        <DialogContent className="sm:max-w-[600px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              Report Generation Failed
            </DialogTitle>
            <DialogDescription>
              The report generation encountered an error and could not be completed.
            </DialogDescription>
          </DialogHeader>

          {errorDialogJob && (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Report ID:</span>
                  <span className="font-mono">{errorDialogJob.jobId}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <StatusBadge status={errorDialogJob.status} />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Created:</span>
                  <span>{formatDate(errorDialogJob.createdAt)}</span>
                </div>
                {errorDialogJob.progress !== undefined && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress:</span>
                    <span>{errorDialogJob.progress}%</span>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                <h4 className="font-medium text-sm mb-2 text-destructive">Error Details:</h4>
                <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">
                  {errorDialogJob.error || 'An unknown error occurred during report generation.'}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setErrorDialogJob(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportLibrary;
